import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  Calendar,
  Clock,
  Share2,
  ExternalLink,
  Newspaper,
  Star,
  CheckCircle2,
} from "lucide-react";
import { EditorState, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";

import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonDialog, CommonButton, CommonChip } from "../../../../Components/ui";

const isFeaturedArticle = (item) => {
  if (!item) return false;
  return (
    item.featured === true ||
    item.featured === 1 ||
    item.featured === "1" ||
    item.featured === "true" ||
    item.isFeatured === true ||
    item.isFeatured === 1 ||
    item.isFeatured === "1" ||
    item.isFeatured === "true"
  );
};

const NewsArticleModal = ({
  open,
  onClose,
  article,
  onShare,
  onOpenPage,
  formatTimeAgo,
  formatPublishDate,
}) => {
  if (!article) return null;

  const {
    title,
    description,
    imageUrl,
    updatedOn,
    createdOn,
  } = article;

  const isFeatured = isFeaturedArticle(article);
  const displayDate = updatedOn || createdOn;

  // Build draft-js EditorState from html description for clean, safe rendering
  let editorState = EditorState.createEmpty();
  if (description) {
    try {
      const contentBlock = htmlToDraft(description);
      if (contentBlock && contentBlock.contentBlocks) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
          contentBlock.entityMap
        );
        editorState = EditorState.createWithContent(contentState);
      }
    } catch (e) {
      console.error("Draft preview parsing error:", e);
    }
  }

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      disableScrollLock={false}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Newspaper size={20} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 700, color: colors.slate[900], fontSize: "1.1rem" }}
            >
              Autobet Newsroom
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Official Article Reader
            </Typography>
          </Box>
        </Box>
      }
      showCloseButton
      maxWidth="md"
      fullWidth
      actions={
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 1 }}>
          <CommonButton
            variant="outline"
            size="small"
            startIcon={<Share2 size={15} />}
            onClick={() => onShare(article)}
          >
            Share Article
          </CommonButton>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <CommonButton
              variant="outline"
              size="small"
              endIcon={<ExternalLink size={14} />}
              onClick={() => onOpenPage(article)}
            >
              Open Dedicated Page
            </CommonButton>

            <CommonButton variant="primary" size="small" onClick={onClose}>
              Close
            </CommonButton>
          </Stack>
        </Box>
      }
    >
      <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
        {/* Badges & Meta Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {isFeatured ? (
            <CommonChip
              label="Featured Story"
              startIcon={<Star size={12} fill={colors.amber[500]} color={colors.amber[500]} />}
              size="small"
              sx={{
                bgcolor: colors.amber[50],
                color: colors.amber[800],
                fontWeight: 700,
                border: `1px solid ${colors.amber[200]}`,
              }}
            />
          ) : (
            <CommonChip
              label="Official Announcement"
              startIcon={<CheckCircle2 size={12} color={colors.primary.main} />}
              size="small"
              sx={{
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                fontWeight: 700,
                border: `1px solid ${colors.blue[200]}`,
              }}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <Calendar size={14} color={colors.slate[400]} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {formatPublishDate(displayDate)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <Clock size={14} color={colors.slate[400]} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {formatTimeAgo(displayDate)}
            </Typography>
          </Box>
        </Box>

        {/* Article Headline */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            lineHeight: 1.3,
            mb: 2.5,
            fontSize: { xs: "1.35rem", sm: "1.85rem" },
          }}
        >
          {title}
        </Typography>

        {/* Cover Image */}
        {imageUrl && (
          <Box
            sx={{
              width: "100%",
              maxHeight: 380,
              borderRadius: borderRadiusPx.xl,
              overflow: "hidden",
              mb: 3,
              bgcolor: colors.slate[100],
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: customShadows.cardFlat,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              sx={{
                width: "100%",
                height: "100%",
                maxHeight: 380,
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 3, borderColor: colors.dashboard.tableBorder }} />

        {/* Article Rich Text Content */}
        <Box
          sx={{
            fontSize: "1rem",
            color: colors.slate[700],
            lineHeight: 1.75,
            "& .rdw-editor-main": {
              p: 0,
            },
            "& p": {
              mb: 1.75,
            },
            "& h1, & h2, & h3, & h4": {
              fontWeight: 700,
              color: colors.slate[900],
              mt: 2.5,
              mb: 1.25,
            },
            "& a": {
              color: colors.primary.main,
              textDecoration: "underline",
            },
            "& ul, & ol": {
              pl: 3,
              mb: 2,
            },
            "& li": {
              mb: 0.75,
            },
            "& blockquote": {
              borderLeft: `4px solid ${colors.primary.main}`,
              pl: 2,
              py: 0.5,
              my: 2,
              fontStyle: "italic",
              bgcolor: colors.blue[50],
              borderRadius: `0 ${borderRadiusPx.md} ${borderRadiusPx.md} 0`,
            },
          }}
        >
          <Editor editorState={editorState} toolbarHidden readOnly />
        </Box>
      </Box>
    </CommonDialog>
  );
};

NewsArticleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  article: PropTypes.object,
  onShare: PropTypes.func.isRequired,
  onOpenPage: PropTypes.func.isRequired,
  formatTimeAgo: PropTypes.func.isRequired,
  formatPublishDate: PropTypes.func.isRequired,
};

export default NewsArticleModal;
